import { AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react'
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react'
import {
  formatAmount,
  formatAmountLive,
  parseAmount,
} from '../../utils/amountFormatter'
import type {
  CT600FinancialDataForm,
  CT600BalanceSheetForm,
  CT600PreviousYearForm,
} from '../../types/ct600Filing'

interface CollapsibleProps {
  title: string
  children: ReactNode
  defaultOpen?: boolean
}

function Section({ title, children, defaultOpen = false }: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  return (
    <div className='rounded-lg border border-gray-200 bg-white'>
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        className='flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors'
      >
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        {isOpen ? (
          <ChevronUp className='h-5 w-5 text-gray-500' />
        ) : (
          <ChevronDown className='h-5 w-5 text-gray-500' />
        )}
      </button>
      {isOpen && (
        <div className='border-t border-gray-100 px-6 pb-6 pt-4'>
          {children}
        </div>
      )}
    </div>
  )
}

interface CurrencyInputProps {
  label: string
  value: string
  onChange: (val: string) => void
  readOnly?: boolean
  highlight?: boolean
  signNormalize?: boolean
  asLoss?: boolean
  hint?: string
  /** Hide the per-input label (used when label is rendered separately for a paired row). */
  hideLabel?: boolean
}

function CurrencyInput({
  label,
  value,
  onChange,
  readOnly = false,
  highlight = false,
  signNormalize = false,
  asLoss = false,
  hint,
  hideLabel = false,
}: CurrencyInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const cursorRef = useRef<number>(0)

  const numeric = parseFloat(value || '0')
  const isLossDisplay = asLoss && numeric < 0
  const displayBase = readOnly
    ? value
      ? formatAmount(Math.abs(numeric))
      : ''
    : value
      ? formatAmountLive(value)
      : ''

  useLayoutEffect(() => {
    const el = inputRef.current
    if (el && document.activeElement === el) {
      el.setSelectionRange(cursorRef.current, cursorRef.current)
    }
  }, [displayBase])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const el = e.target
    const pos = el.selectionStart || 0
    let digitsBefore = 0
    for (let i = 0; i < pos; i++) {
      if (el.value[i] !== ',') digitsBefore++
    }
    let raw = parseAmount(el.value)
    if (signNormalize && raw.startsWith('-')) {
      raw = raw.replace(/^-+/, '')
    }
    const formatted = raw ? formatAmountLive(raw) : ''
    let newPos = formatted.length
    let count = 0
    for (let i = 0; i < formatted.length; i++) {
      if (formatted[i] !== ',') count++
      if (count === digitsBefore) {
        newPos = i + 1
        break
      }
    }
    cursorRef.current = newPos
    onChange(raw)
  }

  return (
    <div>
      {!hideLabel && (
        <label className='mb-2 block text-sm font-medium text-gray-900'>
          {label}
          {isLossDisplay && (
            <span className='ml-2 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700'>
              Loss
            </span>
          )}
        </label>
      )}
      <div className='relative'>
        <span className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400'>
          £
        </span>
        <input
          ref={inputRef}
          type='text'
          value={displayBase}
          onChange={handleChange}
          onBlur={e => {
            let clean = parseAmount(e.target.value)
            if (signNormalize && clean.startsWith('-'))
              clean = clean.replace(/^-+/, '')
            if (clean) {
              const num = parseFloat(clean)
              if (!isNaN(num)) onChange(num.toFixed(2))
            }
          }}
          readOnly={readOnly}
          placeholder='0.00'
          className={`w-full rounded-lg border px-4 py-2.5 pl-8 text-sm text-right ${
            readOnly
              ? isLossDisplay
                ? 'border-red-200 bg-red-50 font-semibold text-red-900'
                : 'border-gray-200 bg-gray-50 text-gray-600'
              : highlight
                ? 'border-blue-300 bg-blue-50 font-semibold text-blue-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                : 'border-gray-300 text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
          }`}
        />
      </div>
      {hint && <p className='mt-1 text-[11px] text-gray-500'>{hint}</p>}
    </div>
  )
}

/**
 * Renders a labelled row with a current-year cell and a previous-year cell.
 * The previous-year cell is omitted (em-dash) for fields not in the
 * `CT600PreviousYearForm` schema, so the rows stay aligned visually.
 */
interface YearRowProps {
  label: string
  hint?: string
  current: Omit<CurrencyInputProps, 'label' | 'hideLabel'>
  previous?: Omit<CurrencyInputProps, 'label' | 'hideLabel'>
}

function YearRow({ label, hint, current, previous }: YearRowProps) {
  return (
    <div className='grid grid-cols-[1fr_1fr] gap-4'>
      <div>
        <label className='mb-2 block text-sm font-medium text-gray-900'>
          {label}
        </label>
        <CurrencyInput {...current} label={label} hideLabel />
        {hint && <p className='mt-1 text-[11px] text-gray-500'>{hint}</p>}
      </div>
      <div>
        <div
          aria-hidden='true'
          className='mb-2 block text-sm font-medium text-gray-900'
        >
          &nbsp;
        </div>
        {previous ? (
          <CurrencyInput {...previous} label={label} hideLabel />
        ) : (
          <div className='flex h-[42px] items-center justify-end rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-sm text-gray-400'>
            —
          </div>
        )}
      </div>
    </div>
  )
}

function YearColumnHeaders({
  currentLabel,
  previousLabel,
}: {
  currentLabel: string
  previousLabel: string
}) {
  return (
    <div className='grid grid-cols-[1fr_1fr] gap-4 mb-3'>
      <div className='rounded-md bg-gray-100 px-4 py-2 text-center text-sm font-semibold text-gray-900'>
        {currentLabel}{' '}
        <span className='ml-1 font-normal text-gray-500'>(current)</span>
      </div>
      <div className='rounded-md bg-gray-100 px-4 py-2 text-center text-sm font-semibold text-gray-900'>
        {previousLabel}{' '}
        <span className='ml-1 font-normal text-gray-500'>(previous)</span>
      </div>
    </div>
  )
}

interface Props {
  financialData: CT600FinancialDataForm
  balanceSheet: CT600BalanceSheetForm
  previousYear: CT600PreviousYearForm
  onFinancialDataChange: (update: CT600FinancialDataForm) => void
  onBalanceSheetChange: (update: CT600BalanceSheetForm) => void
  onPreviousYearChange: (update: CT600PreviousYearForm) => void
  /** Period end date (ISO YYYY-MM-DD) — used to label the year columns. */
  accountingPeriodEnd: string
}

export default function CT600StepFinancials({
  financialData,
  balanceSheet,
  previousYear,
  onFinancialDataChange,
  onBalanceSheetChange,
  onPreviousYearChange,
  accountingPeriodEnd,
}: Props) {
  const p = (v: string) => parseFloat(v) || 0

  // Derive year labels from the period end. Fallback to "Current"/"Previous"
  // when the period end isn't known yet (e.g. on a fresh draft).
  const { currentYearLabel, previousYearLabel } = useMemo(() => {
    const yearFromIso = (iso: string) => {
      if (!iso) return ''
      const m = /^(\d{4})/.exec(iso)
      return m ? m[1] : ''
    }
    const year = yearFromIso(accountingPeriodEnd)
    if (!year)
      return {
        currentYearLabel: 'Current Year',
        previousYearLabel: 'Previous Year',
      }
    const prev = String(Number(year) - 1)
    return { currentYearLabel: year, previousYearLabel: prev }
  }, [accountingPeriodEnd])

  // Auto-calculated P&L fields (current year).
  const plCalc = useMemo(() => {
    const totalIncome = p(financialData.turnover) + p(financialData.otherIncome)
    const grossProfit = totalIncome - p(financialData.costOfSales)
    const totalOperatingExpenses =
      p(financialData.administrativeExpenses) +
      p(financialData.distributionCosts)
    const operatingProfit = grossProfit - totalOperatingExpenses
    const profitBeforeTax =
      operatingProfit +
      p(financialData.interestReceivable) -
      p(financialData.interestPayable)
    const profitAfterTax = profitBeforeTax - p(financialData.taxExpense)
    return {
      totalIncome,
      grossProfit,
      totalOperatingExpenses,
      operatingProfit,
      profitBeforeTax,
      profitAfterTax,
    }
  }, [financialData])

  // Previous-year P&L — derive the same shape as plCalc so every line of the
  // table can show a comparative figure that updates when the user edits the
  // prior-year inputs.
  const prevPlCalc = useMemo(() => {
    const fd = previousYear.financialData
    const totalIncome = p(fd.turnover) + p(fd.otherIncome)
    const grossProfit = totalIncome - p(fd.costOfSales)
    const totalOperatingExpenses =
      p(fd.administrativeExpenses) + p(fd.distributionCosts)
    const operatingProfit = grossProfit - totalOperatingExpenses
    const profitBeforeTax =
      operatingProfit + p(fd.interestReceivable) - p(fd.interestPayable)
    const profitAfterTax = profitBeforeTax - p(fd.ukTaxOnProfits)
    return {
      totalIncome,
      grossProfit,
      totalOperatingExpenses,
      operatingProfit,
      profitBeforeTax,
      profitAfterTax,
    }
  }, [previousYear.financialData])

  // Auto-calculated Balance Sheet fields (current year).
  const bsCalc = useMemo(() => {
    const totalFixedAssets =
      p(balanceSheet.intangibleAssets) + p(balanceSheet.tangibleAssets)
    const totalCurrentAssets =
      p(balanceSheet.stocks) +
      p(balanceSheet.debtors) +
      p(balanceSheet.cashAtBankAndInHand)
    const totalAssets = totalFixedAssets + totalCurrentAssets
    const netCurrentAssets =
      totalCurrentAssets - p(balanceSheet.creditorsDueWithinOneYear)
    const totalLiabilities =
      p(balanceSheet.creditorsDueWithinOneYear) +
      p(balanceSheet.creditorsDueAfterOneYear)
    const netAssets = totalAssets - totalLiabilities
    const shareholdersFunds =
      p(balanceSheet.calledUpShareCapital) +
      p(balanceSheet.openingProfitLoss) +
      plCalc.profitAfterTax
    return {
      totalFixedAssets,
      totalCurrentAssets,
      totalAssets,
      netCurrentAssets,
      totalLiabilities,
      netAssets,
      shareholdersFunds,
    }
  }, [balanceSheet, plCalc.profitAfterTax])

  const balanceCheck = useMemo(() => {
    const diff = bsCalc.netAssets - bsCalc.shareholdersFunds
    const isBalanced = Math.abs(diff) < 0.005
    return { diff, isBalanced }
  }, [bsCalc.netAssets, bsCalc.shareholdersFunds])

  // Auto-calculated Balance Sheet fields (previous year) — mirrors bsCalc so
  // the 2025 column behaves the same way as the 2026 column.
  const prevBsCalc = useMemo(() => {
    const pbs = previousYear.balanceSheet
    const totalFixedAssets = p(pbs.intangibleAssets) + p(pbs.tangibleAssets)
    const totalCurrentAssets =
      p(pbs.stocks) + p(pbs.debtors) + p(pbs.cashAtBankAndInHand)
    const totalAssets = totalFixedAssets + totalCurrentAssets
    const netCurrentAssets =
      totalCurrentAssets - p(pbs.creditorsDueWithinOneYear)
    const totalLiabilities =
      p(pbs.creditorsDueWithinOneYear) + p(pbs.creditorsDueAfterOneYear)
    const totalAssetsLessCurrentLiabilities =
      totalFixedAssets + netCurrentAssets
    const netAssets = totalAssets - totalLiabilities
    const profitAndLossAccount =
      p(pbs.openingProfitLoss) + prevPlCalc.profitAfterTax
    const shareholdersFunds =
      p(pbs.calledUpShareCapital) + profitAndLossAccount
    return {
      totalFixedAssets,
      totalCurrentAssets,
      totalAssets,
      netCurrentAssets,
      totalAssetsLessCurrentLiabilities,
      totalLiabilities,
      netAssets,
      profitAndLossAccount,
      shareholdersFunds,
    }
  }, [previousYear.balanceSheet, prevPlCalc.profitAfterTax])

  const prevBalanceCheck = useMemo(() => {
    const diff = prevBsCalc.netAssets - prevBsCalc.shareholdersFunds
    const isBalanced = Math.abs(diff) < 0.005
    return { diff, isBalanced }
  }, [prevBsCalc.netAssets, prevBsCalc.shareholdersFunds])

  // Sync derived previous-year summary fields back into state so the existing
  // backend save path (which reads previousYear.balanceSheet.netAssets etc.)
  // continues to send the correct comparative figures.
  const prevDerivedString = useMemo(
    () => ({
      netCurrentAssets: prevBsCalc.netCurrentAssets.toFixed(2),
      totalAssetsLessCurrentLiabilities:
        prevBsCalc.totalAssetsLessCurrentLiabilities.toFixed(2),
      netAssets: prevBsCalc.netAssets.toFixed(2),
      profitAndLossAccount: prevBsCalc.profitAndLossAccount.toFixed(2),
      shareholdersFunds: prevBsCalc.shareholdersFunds.toFixed(2),
    }),
    [prevBsCalc],
  )
  useEffect(() => {
    const pbs = previousYear.balanceSheet
    if (
      pbs.netCurrentAssets === prevDerivedString.netCurrentAssets &&
      pbs.totalAssetsLessCurrentLiabilities ===
        prevDerivedString.totalAssetsLessCurrentLiabilities &&
      pbs.netAssets === prevDerivedString.netAssets &&
      pbs.profitAndLossAccount === prevDerivedString.profitAndLossAccount &&
      pbs.shareholdersFunds === prevDerivedString.shareholdersFunds
    ) {
      return
    }
    onPreviousYearChange({
      ...previousYear,
      balanceSheet: {
        ...previousYear.balanceSheet,
        ...prevDerivedString,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevDerivedString])

  // Previous-year Profit Before Tax is now derived from the detail inputs, but
  // the underlying field is still persisted to the backend. Keep them in sync.
  const prevProfitBeforeTaxString = prevPlCalc.profitBeforeTax.toFixed(2)
  useEffect(() => {
    if (
      previousYear.financialData.profitBeforeTax !== prevProfitBeforeTaxString
    ) {
      onPreviousYearChange({
        ...previousYear,
        financialData: {
          ...previousYear.financialData,
          profitBeforeTax: prevProfitBeforeTaxString,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevProfitBeforeTaxString])

  // Prior year's closing P&L Account is this year's brought-forward balance.
  // Keep current-year openingProfitLoss locked to the previous-year closing input.
  useEffect(() => {
    const prevClosing = previousYear.balanceSheet.profitAndLossAccount
    if (balanceSheet.openingProfitLoss !== prevClosing) {
      onBalanceSheetChange({
        ...balanceSheet,
        openingProfitLoss: prevClosing,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousYear.balanceSheet.profitAndLossAccount])

  // Keep closing P&L Account Reserve in sync with openingProfitLoss + profitAfterTax.
  const closingPnLString = (
    p(balanceSheet.openingProfitLoss) + plCalc.profitAfterTax
  ).toFixed(2)
  useEffect(() => {
    if (balanceSheet.profitAndLossAccount !== closingPnLString) {
      onBalanceSheetChange({
        ...balanceSheet,
        profitAndLossAccount: closingPnLString,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closingPnLString])

  const updateFD = (field: keyof CT600FinancialDataForm, value: string) => {
    onFinancialDataChange({ ...financialData, [field]: value })
  }

  const updateBS = (field: keyof CT600BalanceSheetForm, value: string) => {
    onBalanceSheetChange({ ...balanceSheet, [field]: value })
  }

  const updatePrevFD = (
    field: keyof CT600PreviousYearForm['financialData'],
    value: string
  ) => {
    onPreviousYearChange({
      ...previousYear,
      financialData: { ...previousYear.financialData, [field]: value },
    })
  }

  const updatePrevBS = (
    field: keyof CT600PreviousYearForm['balanceSheet'],
    value: string
  ) => {
    onPreviousYearChange({
      ...previousYear,
      balanceSheet: { ...previousYear.balanceSheet, [field]: value },
    })
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-xl font-bold text-gray-900'>
          Financial Statements
        </h2>
        <p className='mt-1 text-sm text-gray-600'>
          Enter the figures from your company accounts for the current period.
          Comparative figures for the previous year are summary-only and stored
          separately. Calculated fields are shown in blue.
        </p>
      </div>

      <Section title='Profit & Loss Statement' defaultOpen>
        <div className='space-y-6'>
          <YearColumnHeaders
            currentLabel={currentYearLabel}
            previousLabel={previousYearLabel}
          />

          {/* Income */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Income
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Turnover'
                current={{
                  value: financialData.turnover,
                  onChange: v => updateFD('turnover', v),
                }}
                previous={{
                  value: previousYear.financialData.turnover,
                  onChange: v => updatePrevFD('turnover', v),
                }}
              />
              <YearRow
                label='Other Income'
                current={{
                  value: financialData.otherIncome,
                  onChange: v => updateFD('otherIncome', v),
                }}
                previous={{
                  value: previousYear.financialData.otherIncome,
                  onChange: v => updatePrevFD('otherIncome', v),
                }}
              />
              <YearRow
                label='Total Income'
                current={{
                  value: plCalc.totalIncome.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
                previous={{
                  value: prevPlCalc.totalIncome.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
              />
            </div>
          </div>

          {/* Cost of Sales */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Cost of Sales
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Cost of Sales'
                current={{
                  value: financialData.costOfSales,
                  onChange: v => updateFD('costOfSales', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.financialData.costOfSales,
                  onChange: v => updatePrevFD('costOfSales', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Gross Profit'
                current={{
                  value: plCalc.grossProfit.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
                previous={{
                  value: prevPlCalc.grossProfit.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
              />
            </div>
          </div>

          {/* Operating Expenses */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Operating Expenses
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Administrative Expenses'
                current={{
                  value: financialData.administrativeExpenses,
                  onChange: v => updateFD('administrativeExpenses', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.financialData.administrativeExpenses,
                  onChange: v => updatePrevFD('administrativeExpenses', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Distribution Costs'
                current={{
                  value: financialData.distributionCosts,
                  onChange: v => updateFD('distributionCosts', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.financialData.distributionCosts,
                  onChange: v => updatePrevFD('distributionCosts', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Total Operating Expenses'
                current={{
                  value: plCalc.totalOperatingExpenses.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
                previous={{
                  value: prevPlCalc.totalOperatingExpenses.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
              />
              <YearRow
                label='Operating Profit'
                current={{
                  value: plCalc.operatingProfit.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevPlCalc.operatingProfit.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
            </div>
          </div>

          {/* Other Items */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Other Items
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Interest Receivable'
                current={{
                  value: financialData.interestReceivable,
                  onChange: v => updateFD('interestReceivable', v),
                }}
                previous={{
                  value: previousYear.financialData.interestReceivable,
                  onChange: v => updatePrevFD('interestReceivable', v),
                }}
              />
              <YearRow
                label='Interest Payable'
                current={{
                  value: financialData.interestPayable,
                  onChange: v => updateFD('interestPayable', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.financialData.interestPayable,
                  onChange: v => updatePrevFD('interestPayable', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Tax Expense (corporation tax only)'
                hint='Used to compute Profit After Tax for the year.'
                current={{
                  value: financialData.taxExpense,
                  onChange: v => updateFD('taxExpense', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.financialData.ukTaxOnProfits,
                  onChange: v => updatePrevFD('ukTaxOnProfits', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Profit / (Loss) Before Tax'
                current={{
                  value: plCalc.profitBeforeTax.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevPlCalc.profitBeforeTax.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
            </div>
          </div>

          {/* Headline figures — one column per year, metrics stacked within */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <PlHeadlineColumn
              yearLabel={currentYearLabel}
              profitBeforeTax={plCalc.profitBeforeTax}
              profitAfterTax={plCalc.profitAfterTax}
            />
            <PlHeadlineColumn
              yearLabel={previousYearLabel}
              profitBeforeTax={prevPlCalc.profitBeforeTax}
              profitAfterTax={prevPlCalc.profitAfterTax}
            />
          </div>
        </div>
      </Section>

      <Section title='Balance Sheet'>
        <div className='space-y-6'>
          <YearColumnHeaders
            currentLabel={currentYearLabel}
            previousLabel={previousYearLabel}
          />

          {/* Fixed Assets */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Fixed Assets
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Intangible Assets'
                current={{
                  value: balanceSheet.intangibleAssets,
                  onChange: v => updateBS('intangibleAssets', v),
                }}
                previous={{
                  value: previousYear.balanceSheet.intangibleAssets,
                  onChange: v => updatePrevBS('intangibleAssets', v),
                }}
              />
              <YearRow
                label='Tangible Assets'
                current={{
                  value: balanceSheet.tangibleAssets,
                  onChange: v => updateBS('tangibleAssets', v),
                }}
                previous={{
                  value: previousYear.balanceSheet.tangibleAssets,
                  onChange: v => updatePrevBS('tangibleAssets', v),
                }}
              />
              <YearRow
                label='Total Fixed Assets'
                current={{
                  value: bsCalc.totalFixedAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
                previous={{
                  value: prevBsCalc.totalFixedAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
              />
            </div>
          </div>

          {/* Current Assets */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Current Assets
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Stocks'
                current={{
                  value: balanceSheet.stocks,
                  onChange: v => updateBS('stocks', v),
                }}
                previous={{
                  value: previousYear.balanceSheet.stocks,
                  onChange: v => updatePrevBS('stocks', v),
                }}
              />
              <YearRow
                label='Debtors'
                current={{
                  value: balanceSheet.debtors,
                  onChange: v => updateBS('debtors', v),
                }}
                previous={{
                  value: previousYear.balanceSheet.debtors,
                  onChange: v => updatePrevBS('debtors', v),
                }}
              />
              <YearRow
                label='Cash at Bank and in Hand'
                current={{
                  value: balanceSheet.cashAtBankAndInHand,
                  onChange: v => updateBS('cashAtBankAndInHand', v),
                }}
                previous={{
                  value: previousYear.balanceSheet.cashAtBankAndInHand,
                  onChange: v => updatePrevBS('cashAtBankAndInHand', v),
                }}
              />
              <YearRow
                label='Total Current Assets'
                current={{
                  value: bsCalc.totalCurrentAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
                previous={{
                  value: prevBsCalc.totalCurrentAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
              />
            </div>
            {/* Per-year Total Assets summary */}
            <div className='mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <TotalAssetsCard
                yearLabel={currentYearLabel}
                amount={bsCalc.totalAssets}
              />
              <TotalAssetsCard
                yearLabel={previousYearLabel}
                amount={prevBsCalc.totalAssets}
              />
            </div>
          </div>

          {/* Liabilities */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Liabilities
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Total Creditors Due Within One Year'
                current={{
                  value: balanceSheet.creditorsDueWithinOneYear,
                  onChange: v => updateBS('creditorsDueWithinOneYear', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.balanceSheet.creditorsDueWithinOneYear,
                  onChange: v => updatePrevBS('creditorsDueWithinOneYear', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Net Current Assets / (Liabilities)'
                current={{
                  value: bsCalc.netCurrentAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevBsCalc.netCurrentAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
              <YearRow
                label='Total Creditors Due After One Year'
                current={{
                  value: balanceSheet.creditorsDueAfterOneYear,
                  onChange: v => updateBS('creditorsDueAfterOneYear', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.balanceSheet.creditorsDueAfterOneYear,
                  onChange: v => updatePrevBS('creditorsDueAfterOneYear', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Total Liabilities'
                current={{
                  value: bsCalc.totalLiabilities.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
                previous={{
                  value: prevBsCalc.totalLiabilities.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                }}
              />
              <YearRow
                label='Total Assets Less Current Liabilities'
                current={{
                  value: (
                    bsCalc.totalFixedAssets + bsCalc.netCurrentAssets
                  ).toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value:
                    prevBsCalc.totalAssetsLessCurrentLiabilities.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
              <YearRow
                label='Net Assets'
                current={{
                  value: bsCalc.netAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevBsCalc.netAssets.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
            </div>
            {/* Per-year Net Assets / (Liabilities) summary */}
            <div className='mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <NetAssetsCard
                yearLabel={currentYearLabel}
                amount={bsCalc.netAssets}
              />
              <NetAssetsCard
                yearLabel={previousYearLabel}
                amount={prevBsCalc.netAssets}
              />
            </div>
          </div>

          {/* Capital & Reserves */}
          <div>
            <h4 className='mb-3 text-sm font-semibold text-gray-700 uppercase tracking-wide'>
              Capital &amp; Reserves
            </h4>
            <div className='space-y-4'>
              <YearRow
                label='Called-Up Share Capital'
                hint='Cannot be negative.'
                current={{
                  value: balanceSheet.calledUpShareCapital,
                  onChange: v => updateBS('calledUpShareCapital', v),
                  signNormalize: true,
                }}
                previous={{
                  value: previousYear.balanceSheet.calledUpShareCapital,
                  onChange: v => updatePrevBS('calledUpShareCapital', v),
                  signNormalize: true,
                }}
              />
              <YearRow
                label='Opening Profit & Loss (brought forward)'
                hint='Current year auto-populates from the prior year&rsquo;s closing P&L Account.'
                current={{
                  value: balanceSheet.openingProfitLoss,
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: previousYear.balanceSheet.openingProfitLoss,
                  onChange: v => updatePrevBS('openingProfitLoss', v),
                  asLoss: true,
                }}
              />
              <YearRow
                label='Profit / (Loss) After Tax for the year'
                current={{
                  value: plCalc.profitAfterTax.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevPlCalc.profitAfterTax.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
              <YearRow
                label='Profit & Loss Account (closing)'
                current={{
                  value: balanceSheet.profitAndLossAccount,
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevBsCalc.profitAndLossAccount.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
              <YearRow
                label="Total Shareholders' Funds"
                current={{
                  value: bsCalc.shareholdersFunds.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
                previous={{
                  value: prevBsCalc.shareholdersFunds.toFixed(2),
                  onChange: () => {},
                  readOnly: true,
                  highlight: true,
                  asLoss: true,
                }}
              />
            </div>
          </div>

          {/* Per-year balance check — side-by-side columns so each year shows
              its own balance status clearly */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <BalanceCheckCard
              yearLabel={currentYearLabel}
              netAssets={bsCalc.netAssets}
              shareholdersFunds={bsCalc.shareholdersFunds}
              isBalanced={balanceCheck.isBalanced}
              diff={balanceCheck.diff}
              gating
            />
            <BalanceCheckCard
              yearLabel={previousYearLabel}
              netAssets={prevBsCalc.netAssets}
              shareholdersFunds={prevBsCalc.shareholdersFunds}
              isBalanced={prevBalanceCheck.isBalanced}
              diff={prevBalanceCheck.diff}
            />
          </div>
        </div>
      </Section>
    </div>
  )
}

function TotalAssetsCard({
  yearLabel,
  amount,
}: {
  yearLabel: string
  amount: number
}) {
  return (
    <div className='rounded-lg border border-blue-200 bg-blue-50 p-3'>
      <p className='mb-1 text-[10px] font-semibold uppercase tracking-wide text-blue-700'>
        {yearLabel}
      </p>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-semibold text-blue-900'>Total Assets</span>
        <span className='text-lg font-bold text-blue-900'>
          £{formatAmount(amount)}
        </span>
      </div>
    </div>
  )
}

function NetAssetsCard({
  yearLabel,
  amount,
}: {
  yearLabel: string
  amount: number
}) {
  const negative = amount < 0
  return (
    <div
      className={`rounded-lg border-2 p-3 ${
        negative ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
      }`}
    >
      <p
        className={`mb-1 text-[10px] font-semibold uppercase tracking-wide ${
          negative ? 'text-red-700' : 'text-blue-700'
        }`}
      >
        {yearLabel}
      </p>
      <div className='flex items-center justify-between'>
        <span
          className={`text-sm font-semibold ${
            negative ? 'text-red-900' : 'text-blue-900'
          }`}
        >
          {negative ? 'Net Liabilities' : 'Net Assets'}
        </span>
        <span
          className={`text-lg font-bold ${
            negative ? 'text-red-900' : 'text-blue-900'
          }`}
        >
          £{formatAmount(Math.abs(amount))}
        </span>
      </div>
    </div>
  )
}

function BalanceCheckCard({
  yearLabel,
  netAssets,
  shareholdersFunds,
  isBalanced,
  diff,
  gating = false,
}: {
  yearLabel: string
  netAssets: number
  shareholdersFunds: number
  isBalanced: boolean
  diff: number
  gating?: boolean
}) {
  return (
    <div>
      <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {yearLabel}
      </p>
      <div
        className={`rounded-lg border-2 p-4 h-full ${
          isBalanced
            ? 'border-green-200 bg-green-50'
            : 'border-red-200 bg-red-50'
        }`}
      >
        <div className='flex items-start gap-3'>
          {isBalanced ? (
            <CheckCircle className='h-5 w-5 mt-0.5 flex-shrink-0 text-green-600' />
          ) : (
            <AlertCircle className='h-5 w-5 mt-0.5 flex-shrink-0 text-red-600' />
          )}
          <div className='flex-1'>
            <p
              className={`text-sm font-semibold ${
                isBalanced ? 'text-green-900' : 'text-red-900'
              }`}
            >
              {isBalanced
                ? 'Balance sheet balances'
                : 'Balance sheet does not balance'}
            </p>
            <p
              className={`mt-0.5 text-xs ${
                isBalanced ? 'text-green-800' : 'text-red-800'
              }`}
            >
              Net Assets must equal Total Shareholders&apos; Funds. Net Assets:
              £{formatAmount(netAssets)} · Shareholders&apos; Funds: £
              {formatAmount(shareholdersFunds)}
              {!isBalanced && (
                <>
                  {' '}
                  · Difference:{' '}
                  <strong>£{formatAmount(Math.abs(diff))}</strong>
                </>
              )}
            </p>
            {!isBalanced && gating && (
              <p className='mt-1 text-xs text-red-800'>
                You won&apos;t be able to move on until the balance sheet
                balances. Re-check your figures (especially Opening P&amp;L and
                Capital &amp; Reserves).
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function PlHeadlineColumn({
  yearLabel,
  profitBeforeTax,
  profitAfterTax,
}: {
  yearLabel: string
  profitBeforeTax: number
  profitAfterTax: number
}) {
  return (
    <div>
      <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 text-center'>
        {yearLabel}
      </p>
      <div className='space-y-3'>
        <HeadlineCard
          label={profitBeforeTax < 0 ? 'Loss Before Tax' : 'Profit Before Tax'}
          amount={profitBeforeTax}
        />
        <HeadlineCard
          label={profitAfterTax < 0 ? 'Loss After Tax' : 'Profit After Tax'}
          amount={profitAfterTax}
        />
      </div>
    </div>
  )
}

function HeadlineCard({ label, amount }: { label: string; amount: number }) {
  const negative = amount < 0
  return (
    <div
      className={`rounded-lg border-2 p-4 ${
        negative ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'
      }`}
    >
      <div className='flex items-center justify-between gap-2'>
        <span
          className={`text-base font-semibold ${
            negative ? 'text-red-900' : 'text-blue-900'
          }`}
        >
          {label}
        </span>
        <span
          className={`text-xl font-bold ${
            negative ? 'text-red-900' : 'text-blue-900'
          }`}
        >
          £{formatAmount(Math.abs(amount))}
        </span>
      </div>
    </div>
  )
}
